// An issue's handoff to the coding agent on its page (D103 #13, APS-390): the action bar's
// primary "Hand off to agent [h]", the rail's Agent handoff panel (the latest handoff, its state
// and pull request) and Linked (the GitHub or Linear issue a handoff opened, and the alert rule
// that handed it off). Everything here comes from the issue's handoffs; nothing else links.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { isApiError } from '../api/client'
import {
  alertDestinationsQuery,
  alertRulesQuery,
  issueHandoffsQuery,
  orgQuery,
  useIssueHandoff,
} from '../api/queries'
import type { AlertDestination, IssueHandoff } from '../api/types'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { PRESET_LABEL } from '../lib/alerts'
import { shortcutOff } from '../lib/keys'
import { Section } from './d/Page'
import { Status } from './d/Status'
import { ActionMenu } from './IssueStates'
import { Icon } from './icons'

/** A destination the issue page can hand off to: GitHub or Linear, on, and not revoked. */
export const canHandOffTo = (d: AlertDestination) =>
  (d.kind === 'github' || d.kind === 'linear') && !!d.handoff_enabled_at && !d.revoked_at

/** Newest first, whatever order they come in. */
export const newestFirst = (handoffs: readonly IssueHandoff[]) =>
  [...handoffs].sort((a, b) => b.created_at.localeCompare(a.created_at))

const PR_STATE = {
  draft: { s: 'on', word: 'issue.handoff.state.draft' },
  in_review: { s: 'reg', word: 'issue.handoff.state.inReview' },
  merged: { s: 'ok', word: 'issue.handoff.state.merged' },
  closed: { s: 'mute', word: 'issue.handoff.state.closed', glyph: '–' },
} as const

const HANDOFF_STATE = {
  opened: { s: 'on', word: 'issue.handoff.state.opened' },
  claimed: { s: 'reg', word: 'issue.handoff.state.claimed' },
  failed: { s: 'miss', word: 'issue.handoff.state.failed' },
} as const

/** A handoff's state as a status word: its pull request's when it has one. */
function HandoffState({ handoff }: { handoff: IssueHandoff }) {
  const { t } = useI18n()
  const pr = handoff.pull_request
  const st: { s: 'on' | 'reg' | 'ok' | 'mute' | 'miss'; word: MessageId; glyph?: string } = pr
    ? PR_STATE[pr.state]
    : HANDOFF_STATE[handoff.state]
  return (
    <>
      <Status s={st.s} label={t(st.word)} {...(st.glyph ? { glyph: st.glyph } : {})} />
      {!pr && handoff.state === 'failed' && handoff.error_code ? (
        <span className="mono faint" translate="no">
          {' '}
          {handoff.error_code}
        </span>
      ) : null}
    </>
  )
}

const provider = (p: IssueHandoff['provider']) => (p === 'github' ? 'GitHub' : 'Linear')

/** The rail's Agent handoff panel: the latest handoff, or what a handoff does. */
export function AgentHandoffPanel({
  orgId,
  projectId,
  issueId,
}: {
  orgId: string
  projectId: string
  issueId: string
}) {
  const { t, relative } = useI18n()
  const handoffs = useQuery(issueHandoffsQuery(projectId, issueId))
  const org = useQuery(orgQuery(orgId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const latest = newestFirst(handoffs.data?.handoffs ?? [])[0]
  const canSetUp = org.data?.role === 'owner' || org.data?.role === 'admin'
  const noDestination = destinations.isSuccess && !destinations.data.destinations.some(canHandOffTo)
  const pr = latest?.pull_request
  return (
    <Section
      title={t('issue.handoff.title')}
      meta={latest ? provider(latest.provider) : t('issue.handoff.none')}
    >
      {latest ? (
        <>
          <dl className="kv">
            {pr ? (
              <>
                <dt>{t('issue.handoff.pr')}</dt>
                <dd>
                  <a href={pr.url} target="_blank" rel="noreferrer" translate="no">
                    {t('issue.handoff.prNumber', { number: pr.number })} ↗
                  </a>
                </dd>
              </>
            ) : null}
            <dt>{t('issue.handoff.state')}</dt>
            <dd>
              <HandoffState handoff={latest} />
            </dd>
            <dt>{t('issue.handoff.when')}</dt>
            <dd>{relative(latest.opened_at ?? latest.created_at)}</dd>
          </dl>
          {pr ? (
            <div className="is-btns">
              <a className="btn sm" href={pr.url} target="_blank" rel="noreferrer">
                <Icon name="git" small />
                {t('issue.handoff.review')} ↗
              </a>
            </div>
          ) : null}
        </>
      ) : (
        <p className="is-p">
          {t('issue.handoff.body')}
          {noDestination && !canSetUp && org.isSuccess ? ` ${t('issue.handoff.askOwner')}` : ''}
        </p>
      )}
    </Section>
  )
}

/**
 * Linked: the GitHub issue and the Linear issue the latest handoffs opened, and the alert rule
 * that handed it off. A row without data is left out, and the panel with none.
 */
export function LinkedPanel({
  orgId,
  projectId,
  issueId,
}: {
  orgId: string
  projectId: string
  issueId: string
}) {
  const { t } = useI18n()
  const handoffs = useQuery(issueHandoffsQuery(projectId, issueId))
  const list = newestFirst(handoffs.data?.handoffs ?? [])
  const opened = (p: IssueHandoff['provider']) => list.find((h) => h.provider === p && h.url)
  const github = opened('github')
  const linear = opened('linear')
  const ruleId = list.find((h) => h.rule_id)?.rule_id
  const rules = useQuery({ ...alertRulesQuery(orgId, projectId), enabled: !!ruleId })
  const rule = ruleId ? rules.data?.rules.find((r) => r.id === ruleId) : undefined
  if (!github && !linear && !rule) return null
  const external = (h: IssueHandoff) => (
    <a href={h.url ?? undefined} target="_blank" rel="noreferrer" translate="no">
      {h.ref ?? provider(h.provider)} ↗
    </a>
  )
  return (
    <Section title={t('issue.linked.title')}>
      <dl className="kv">
        {github ? (
          <>
            <dt>{t('issue.linked.github')}</dt>
            <dd>{external(github)}</dd>
          </>
        ) : null}
        {linear ? (
          <>
            <dt>{t('issue.linked.linear')}</dt>
            <dd>{external(linear)}</dd>
          </>
        ) : null}
        {rule ? (
          <>
            <dt>{t('issue.linked.rule')}</dt>
            <dd>
              <Link
                to="/o/$orgId/p/$projectId/alerts/$ruleId"
                params={{ orgId, projectId, ruleId: rule.id }}
              >
                {t(PRESET_LABEL[rule.preset])}
              </Link>
            </dd>
          </>
        ) : null}
      </dl>
    </Section>
  )
}

/** Why a handoff did not go through. */
function handoffError(t: Translator['t'], error: unknown): string {
  if (isApiError(error, 409)) return t('issue.handoff.error.off')
  if (isApiError(error, 403)) return t('issue.handoff.error.forbidden')
  if (isApiError(error, 429)) return t('issues.state.error.rateLimited')
  return t('issue.handoff.error.failed')
}

/**
 * The action bar's primary: hand the issue off now (one destination, or a menu of several; `h`
 * either way). With none, owners and admins get a plain link to set one up; members nothing.
 */
export function HandOffAction({
  orgId,
  projectId,
  issueId,
  notify,
  primary = true,
}: {
  orgId: string
  projectId: string
  issueId: string
  /** The page's toast: said once the handoff went through, or why it did not. */
  notify: (message: string) => void
  /** The bar's primary while there is something to fix; a plain button once resolved or ignored. */
  primary?: boolean
}) {
  const { t } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const handoff = useIssueHandoff(orgId, projectId)
  const usable = (destinations.data?.destinations ?? []).filter(canHandOffTo)
  const only = usable.length === 1 ? usable[0] : undefined
  const handOff = (d: AlertDestination) =>
    handoff.mutate(
      { issueId, destinationId: d.id },
      {
        onSuccess: () => notify(t('issue.handoff.done', { name: d.name })),
        onError: (error) => notify(handoffError(t, error)),
      },
    )
  const single = useRef(() => {})
  single.current = () => {
    if (only && !handoff.isPending) handOff(only)
  }
  useEffect(() => {
    if (!only) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'h' || shortcutOff(e)) return
      e.preventDefault()
      single.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [only])

  if (!destinations.isSuccess) return null
  const label = (
    <>
      <Icon name="agent" />
      {t('issue.handoff.action')}
    </>
  )
  if (only)
    return (
      <button
        type="button"
        className={primary ? 'btn solid' : 'btn'}
        aria-keyshortcuts="h"
        disabled={handoff.isPending}
        onClick={() => handOff(only)}
      >
        {label}{' '}
        <span aria-hidden="true">
          <kbd translate="no">h</kbd>
        </span>
      </button>
    )
  if (usable.length > 1)
    return (
      <ActionMenu
        small={false}
        solid={primary}
        shortcut="h"
        label={label}
        disabled={handoff.isPending}
        items={usable.map((d) => ({
          key: d.id,
          label: d.name,
          verbatim: true,
          onSelect: () => handOff(d),
        }))}
      />
    )
  return org.data?.role === 'owner' || org.data?.role === 'admin' ? (
    <Link
      className="btn"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId, projectId, section: 'agents' }}
    >
      {t('issue.handoff.setUp')}
    </Link>
  ) : null
}
